import { Component } from '@theme/component';

// How long the "you've hit the limit" message stays visible before auto-hiding.
const LIMIT_MESSAGE_DURATION = 4000;
// How long a cart error stays on the button before it resets to the default label.
const ERROR_MESSAGE_DURATION = 5000;

/**
 * @typedef {object} BuildYourOwnCollectionRefs
 * @property {HTMLButtonElement[]} cards
 * @property {HTMLElement} counter
 * @property {HTMLElement} counterValue
 * @property {HTMLElement} limitMessage
 * @property {HTMLButtonElement} submitButton
 */

/**
 * <build-your-own-collection>
 *
 * Lets a customer select exactly `data-required-count` wallpaper cards, then
 * adds one line item (the product at `data-variant-id`) to the cart with the
 * chosen titles stored as `properties[Wallpaper 1..N]`.
 *
 * @extends {Component<BuildYourOwnCollectionRefs>}
 */
class BuildYourOwnCollection extends Component {
  requiredRefs = ['counter', 'counterValue', 'limitMessage', 'submitButton'];

  /** @type {string[]} Ordered list of selected wallpaper titles. */
  #selected = [];

  /** @type {number} */
  #limitMessageTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.requiredCount = Number(this.dataset.requiredCount) || 8;
    this.variantId = this.dataset.variantId;
    this.submitLabel = this.refs.submitButton.textContent?.trim() ?? 'Add My Collection';

    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    window.clearTimeout(this.#limitMessageTimeout);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const card = target.closest('.wallpaper-card');
    if (card instanceof HTMLButtonElement) {
      this.#toggleCard(card);
      return;
    }

    if (target.closest('[ref="submitButton"]')) {
      this.#addToCart();
    }
  };

  /** @param {HTMLButtonElement} card */
  #toggleCard(card) {
    const title = card.dataset.wallpaperTitle ?? '';
    const isSelected = card.getAttribute('aria-pressed') === 'true';

    if (isSelected) {
      this.#selected = this.#selected.filter((selectedTitle) => selectedTitle !== title);
      card.setAttribute('aria-pressed', 'false');
      card.classList.remove('is-selected');
      this.#hideLimitMessage();
    } else {
      if (this.#selected.length >= this.requiredCount) {
        this.#showLimitMessage();
        return;
      }

      this.#selected.push(title);
      card.setAttribute('aria-pressed', 'true');
      card.classList.add('is-selected');
    }

    this.#updateCounter();
  }

  #updateCounter() {
    const count = this.#selected.length;
    const { counter, counterValue, submitButton } = this.refs;

    counterValue.textContent = String(count);

    const complete = count === this.requiredCount;
    counter.classList.toggle('is-complete', complete);
    submitButton.disabled = !complete;
  }

  #showLimitMessage() {
    this.refs.limitMessage.hidden = false;
    window.clearTimeout(this.#limitMessageTimeout);
    this.#limitMessageTimeout = window.setTimeout(() => this.#hideLimitMessage(), LIMIT_MESSAGE_DURATION);
  }

  #hideLimitMessage() {
    this.refs.limitMessage.hidden = true;
    window.clearTimeout(this.#limitMessageTimeout);
  }

  async #addToCart() {
    const { submitButton } = this.refs;
    if (this.#selected.length !== this.requiredCount || !this.variantId) return;

    submitButton.disabled = true;
    submitButton.textContent = 'Adding…';

    /** @type {Record<string, string>} */
    const properties = {};
    this.#selected.forEach((title, index) => {
      properties[`Wallpaper ${index + 1}`] = title;
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.variantId, quantity: 1, properties }],
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add your collection to the cart.');
      }

      window.location.href = Theme.routes.cart_url;
    } catch (error) {
      submitButton.textContent = error instanceof Error ? error.message : 'Something went wrong';
      window.setTimeout(() => {
        submitButton.textContent = this.submitLabel;
        submitButton.disabled = this.#selected.length !== this.requiredCount;
      }, ERROR_MESSAGE_DURATION);
    }
  }
}

if (!customElements.get('build-your-own-collection')) {
  customElements.define('build-your-own-collection', BuildYourOwnCollection);
}
